import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { createTheme, ThemeProvider, CssBaseline, Box, Typography, Button } from "@mui/material";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { OfflineProvider } from "./contexts/OfflineContext";
import Login from "./pages/Login";
import TripList from "./pages/TripList";
import TripDetail from "./pages/TripDetail";
import ExpenseForm from "./pages/ExpenseForm";
import JoinTrip from "./pages/JoinTrip";
import { AdminDashboard, AdminTrips, AdminPending, UserList, UserTrips } from "./pages/AdminPage";

export const HEADER_GRADIENT = "linear-gradient(135deg, #A8D8EA 0%, #FCE38A 100%)";
export const HEADER_TEXT = "#2D3436";

const theme = createTheme({
  palette: {
    primary: { main: "#A8D8EA", contrastText: "#2D3436" },
    background: { default: "#F0F4F7", paper: "rgba(255,255,255,0.8)" },
    text: { primary: "#2D3436", secondary: "#636E72" },
  },
  typography: {
    fontFamily: "'Quicksand', 'Roboto', sans-serif",
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    h6: { fontWeight: 700 },
    body1: { fontWeight: 500 },
    body2: { fontWeight: 500 },
    caption: { fontWeight: 500 },
  },
  shape: { borderRadius: 16 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: "linear-gradient(160deg, #EBF3F8 0%, #F8F3E8 100%)",
          minHeight: "100vh",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          background: "rgba(255,255,255,0.75)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.6)",
          boxShadow: "0 4px 24px rgba(168,216,234,0.15)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          background: "rgba(255,255,255,0.75)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        transitionDuration: 0,
        disableScrollLock: true,
        BackdropProps: { transitionDuration: 0 },
      },
      styleOverrides: {
        paper: {
          borderRadius: 28,
          background: "rgba(255,255,255,0.95)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          background: HEADER_GRADIENT,
          color: HEADER_TEXT,
          fontWeight: 700,
          borderRadius: 50,
          boxShadow: "0 4px 16px rgba(168,216,234,0.45)",
          "&:hover": {
            background: "linear-gradient(135deg, #8ECEE0 0%, #F0D478 100%)",
            boxShadow: "0 6px 20px rgba(168,216,234,0.55)",
          },
        },
        outlinedPrimary: {
          borderRadius: 50,
          borderColor: "#A8D8EA",
          color: "#2D3436",
          fontWeight: 700,
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        primary: {
          background: HEADER_GRADIENT,
          color: HEADER_TEXT,
          width: 60,
          height: 60,
          boxShadow: "0 8px 28px rgba(168,216,234,0.55)",
          "&:hover": {
            background: "linear-gradient(135deg, #8ECEE0 0%, #F0D478 100%)",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 16,
            background: "rgba(255,255,255,0.6)",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 20, fontFamily: "'Quicksand', sans-serif", fontWeight: 600 },
      },
    },
  },
});


function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// MUI sets aria-hidden="true" on #root whenever any Modal (Dialog, Menu,
// Popover) opens. On iOS WebKit this blocks ALL touch events on the entire app.
// Rather than patching cleanup after the fact (which has timing gaps), we
// watch #root itself and strip the attribute the instant MUI sets it.
// Trade-off: screen readers won't have the background hidden during a modal —
// acceptable for this app; focus-trap still works normally.
function MuiModalCleanup() {
  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) return;

    // Strip aria-hidden immediately whenever MUI sets it
    const observer = new MutationObserver(() => {
      if (root.getAttribute("aria-hidden") === "true") {
        root.removeAttribute("aria-hidden");
      }
    });
    observer.observe(root, { attributes: true, attributeFilter: ["aria-hidden"] });

    // Also clean up body overflow locks MUI applies (scroll-lock side-effect)
    const bodyObserver = new MutationObserver(() => {
      if (!document.querySelector('[role="dialog"][aria-modal="true"]')) {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }
    });
    bodyObserver.observe(document.body, { childList: true });

    // iOS BFCache: effects don't re-run on swipe-back restore
    const handlePageShow = (e) => {
      if (e.persisted) {
        root.removeAttribute("aria-hidden");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }
    };
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      observer.disconnect();
      bodyObserver.disconnect();
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  return null;
}

function RequireAuth({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user === undefined) return null;
  if (!user) {
    if (pathname !== "/") sessionStorage.setItem("redirectAfterLogin", pathname);
    return <Navigate to="/login" replace />;
  }
  return children;
}

function RequireAdmin({ children }) {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function NotFound() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: 2, p: 3 }}>
      <Typography fontSize="3rem">🔍</Typography>
      <Typography fontWeight={700} variant="h6" textAlign="center">Page not found.</Typography>
      <Button component="a" href="/" variant="contained">Back to My Trips</Button>
    </Box>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      const redirect = sessionStorage.getItem("redirectAfterLogin");
      if (redirect) {
        sessionStorage.removeItem("redirectAfterLogin");
        navigate(redirect, { replace: true });
      }
    }
  }, [user, navigate]);

  if (user === undefined) return null;
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/" element={<RequireAuth><TripList /></RequireAuth>} />
      <Route path="/trips/:tripId" element={<RequireAuth><TripDetail /></RequireAuth>} />
      <Route path="/trips/:tripId/expenses/new" element={<RequireAuth><ExpenseForm /></RequireAuth>} />
      <Route path="/trips/:tripId/expenses/:expenseId/edit" element={<RequireAuth><ExpenseForm /></RequireAuth>} />
      <Route path="/admin" element={<RequireAuth><RequireAdmin><AdminDashboard /></RequireAdmin></RequireAuth>} />
      <Route path="/admin/trips" element={<RequireAuth><RequireAdmin><AdminTrips /></RequireAdmin></RequireAuth>} />
      <Route path="/admin/pending" element={<RequireAuth><RequireAdmin><AdminPending /></RequireAdmin></RequireAuth>} />
      <Route path="/admin/users" element={<RequireAuth><RequireAdmin><UserList /></RequireAdmin></RequireAuth>} />
      <Route path="/admin/users/:userId" element={<RequireAuth><RequireAdmin><UserTrips /></RequireAdmin></RequireAuth>} />
<Route path="/:code" element={<RequireAuth><JoinTrip /></RequireAuth>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <OfflineProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <MuiModalCleanup />
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </OfflineProvider>
    </ThemeProvider>
  );
}
